"use client";

import { useState } from "react";
import { MerchantMark } from "@/components/MerchantMark";
import { isHex, onColor } from "@/lib/color";
import { saveProfile } from "./actions";
import { useFormAction } from "./useFormAction";

type Props = { name: string; nameAr: string; vatNumber: string | null; brandColor: string; logoUrl: string | null };

/** one line of the printed form: label on one side, the field on the other */
function Row({
  id,
  label,
  hint,
  error,
  children,
}: {
  id?: string;
  label: string;
  hint?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="form-row">
      <label htmlFor={id} className="text-[13px] text-ink-soft">
        {label}
      </label>
      <div className="min-w-0">
        {children}
        {hint}
        {error && (
          <p role="alert" className="mt-1 text-sm text-alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

const hintClass = "mt-1 text-[13px] text-ink-soft";

export function SettingsForm(initial: Props) {
  const [state, onSubmit, pending] = useFormAction(saveProfile);
  const [name, setName] = useState(initial.name);
  const [color, setColor] = useState(initial.brandColor);
  const [preview, setPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const shownColor = isHex(color) ? color : initial.brandColor;
  const logo = removeLogo ? null : (preview ?? initial.logoUrl);
  const err = (f: string) => state?.errors?.[f];

  return (
    <form onSubmit={onSubmit} className="mt-2">
      <Row id="name" label="Business name" error={err("name")}>
        <input id="name" name="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} aria-invalid={!!err("name")} className="slip-input" required />
      </Row>

      <Row id="nameAr" label="Name in Arabic" error={err("nameAr")}>
        <input id="nameAr" name="nameAr" defaultValue={initial.nameAr} dir="rtl" lang="ar" maxLength={80} aria-invalid={!!err("nameAr")} className="slip-input" />
      </Row>

      <Row
        id="vatNumber"
        label="VAT number"
        error={err("vatNumber")}
        hint={
          <p id="vat-hint" className={hintClass}>
            Leave it empty and receipts go out without the ZATCA QR code.
          </p>
        }
      >
        <input
          id="vatNumber"
          name="vatNumber"
          defaultValue={initial.vatNumber ?? ""}
          dir="ltr"
          inputMode="numeric"
          placeholder="15 digits"
          aria-invalid={!!err("vatNumber")}
          aria-describedby="vat-hint"
          className="slip-input num"
        />
      </Row>

      <Row
        id="brandColor"
        label="Brand colour"
        error={err("brandColor")}
        hint={
          <p id="color-hint" className={hintClass}>
            Used on the main button of your receipts.
          </p>
        }
      >
        <div className="flex items-center gap-2.5">
          <input
            type="color"
            aria-label="Pick a colour"
            value={shownColor}
            onChange={(e) => setColor(e.target.value)}
            className="size-7 flex-none cursor-pointer rounded-md border-0 bg-transparent p-0"
          />
          <input
            id="brandColor"
            name="brandColor"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            dir="ltr"
            maxLength={7}
            aria-invalid={!!err("brandColor")}
            aria-describedby="color-hint"
            className="slip-input num"
          />
        </div>
      </Row>

      <Row
        id="logo"
        label="Logo"
        error={err("logo")}
        hint={
          <p id="logo-hint" className={hintClass}>
            PNG, JPEG or WebP, under 512 KB. It prints in one colour on receipts.
          </p>
        }
      >
        <input
          id="logo"
          name="logo"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-invalid={!!err("logo")}
          aria-describedby="logo-hint"
          onChange={(e) => {
            const file = e.target.files?.[0];
            setRemoveLogo(false);
            if (preview) URL.revokeObjectURL(preview);
            setPreview(file ? URL.createObjectURL(file) : null);
          }}
          className="w-full text-sm file:me-3 file:min-h-10 file:cursor-pointer file:rounded-[13px] file:border-2 file:border-line file:bg-transparent file:px-4 file:text-sm file:font-medium"
        />
        {initial.logoUrl && (
          <label className="mt-2 flex min-h-10 items-center gap-3 text-sm">
            <input type="checkbox" name="removeLogo" checked={removeLogo} onChange={(e) => setRemoveLogo(e.target.checked)} className="size-4 accent-ink" />
            Remove the current logo
          </label>
        )}
      </Row>

      {/* what a customer sees: the printed header, and the button in your colour */}
      <div className="form-row" aria-hidden>
        <span className="text-[13px] text-ink-soft">Receipt preview</span>
        <div className="flex items-center gap-4">
          <div className="w-36 flex-none rounded-sm bg-receipt px-3 pt-3.5 pb-3 text-center font-mono text-[11px] shadow-[0_1px_0_rgb(0_0_0/0.05),0_12px_20px_-14px_rgb(0_0_0/0.35)]">
            <MerchantMark name={name || "?"} logoUrl={logo} color={shownColor} print className="mx-auto size-9 rounded-lg" />
            <p className="mt-1.5 text-[12px] font-semibold break-words">{name || "Your business"}</p>
            <hr className="rule" style={{ marginBlock: 8 }} />
            <div className="flex justify-between">
              <span>Item</span>
              <span>12.00</span>
            </div>
          </div>
          <div className="flex h-9 items-center rounded-xl px-3 text-[12px] font-medium" style={{ background: shownColor, color: onColor(shownColor) }}>
            View my receipts
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="submit" disabled={pending} className="inline-flex h-11 items-center rounded-[13px] bg-stamp px-5 text-[14.5px] font-medium text-white disabled:opacity-60">
          {pending ? "Saving" : "Save details"}
        </button>
        {state?.ok && state.message && (
          <p role="status" className="text-sm text-paid">
            {state.message}
          </p>
        )}
      </div>
    </form>
  );
}
